import { CommonModule } from '@angular/common';
import { Component, Input, SimpleChanges } from '@angular/core';
import { AlertController, IonicModule, NavController } from '@ionic/angular';
import {
  AnalyzedConsumption,
  Hours,
  newAnalysisDay,
  SmartScore
} from 'src/app/services/analyse/analyse.types';
import {
  MeteorologyObservations,
  MeteorologyForecast
} from 'src/app/services/meteorologi/meteorologi.types';
import { AnalyticsService } from 'src/app/services/analytics/analytics.service';
import {
  analyseNettleieRoute,
  profilRoute,
  smartScoreRoute
} from 'src/app/routes';
import { SmartScoreComponent } from '../smartscore/smartscore.component';
import { SummaryNettleieComponent } from '../nettleie/summary/summary.component';
import { PredictionNettleieComponent } from '../nettleie/prediction/prediction.component';
import { NettleieAdvisoryComponent } from '../nettleie/advisory/advisory.component';
import { Anlegg } from 'src/app/services/forbruk/forbruk.types';
import { PricesForbrukDashboardComponent } from '../forbruk/prices-forbruk/prices-forbruk-dashboard.component';
import { DayMaxThreeComponent } from '../nettleie/dayMaxThree/dayMaxThree.component';
import { NewMonthNoAnalysisComponent } from '../new-month-no-analysis/new-month-no-analysis.component';
import { hasChanged } from 'src/app/utils/change-utils';
import { formatYYYYMMNumberToDate } from 'src/app/utils/date-utils';
import { Preferences } from '@capacitor/preferences';
import { Router } from '@angular/router';
import { Capacitor } from '@capacitor/core';

@Component({
  selector: 'app-analyse-dashboard',
  standalone: true,
  imports: [
    CommonModule,
    IonicModule,
    SmartScoreComponent,
    SummaryNettleieComponent,
    PredictionNettleieComponent,
    PricesForbrukDashboardComponent,
    NettleieAdvisoryComponent,
    DayMaxThreeComponent,
    NewMonthNoAnalysisComponent
  ],
  template: `
    <ion-row>
      <ion-col>
        @if (isPastSmartScore) {
        <ion-row>
          <ion-col>
            <app-new-month-no-analysis
              [objectId]="valgtAnlegg.objectid"
            ></app-new-month-no-analysis>
          </ion-col>
        </ion-row>
        }
        <ion-row>
          <ion-col>
            <div class="bg-white mb-8 max-md:px-2">
              <div class="flex justify-between items-center">
                <p class="my-0 capitalize text-lg">{{ monthLabel }}</p>
                @if(isPushAvailable) {
                <ion-icon
                  class="text-2xl px-1 hover:cursor-pointer text-[--lysning]"
                  name="notifications"
                  (click)="showNewsAlert()"
                ></ion-icon>
                }
              </div>
              <app-smartscore
                [smartScore]="smartScore"
                [showDescription]="true"
                [hasError]="errorScore"
                [isPastSmartScore]="isPastSmartScore"
              ></app-smartscore>

              @if (smartScore) {
              <p class="my-0">
                <strong>
                  @if (!isPastSmartScore) { Du kan spare } @else { Du kunne ha
                  spart }
                </strong>
              </p>
              <p class="m-0">
                <strong class="text-3xl text-[var(--lysning)]"
                  >{{ savings.toFixed(0) }} kr</strong
                >
                <strong> / mnd </strong>
              </p>
              <div class="my-2">
                <p class="m-0">
                  <strong
                    >{{
                      savingsDelivery > 0 ? savingsDelivery.toFixed(0) : 0
                    }}
                    kr</strong
                  >
                  tilknyttet døgnmaks nettleie
                </p>
                <p class="m-0">
                  <strong
                    >{{
                      savingsConsumption > 0 ? savingsConsumption.toFixed(0) : 0
                    }}
                    kr</strong
                  >
                  når prisen @if (isPastSmartScore) { var } @else { er }
                  rimeligere
                </p>
              </div>
              }
            </div>
          </ion-col>
        </ion-row>

        <ion-row>
          <ion-col>
            <h2 class="ml-2 mt-0">Nettleieanalyse</h2>
            <div class="bg-white pb-0.5 mb-8 rounded shadow-lg">
              <h3 class="bg-[--primary-strong-100] m-0 p-4 rounded-t-lg">
                <strong>
                  {{ savingsDelivery > 0 ? savingsDelivery.toFixed(0) : 0 }} kr
                  å spare på døgnmaks nettleie</strong
                >
              </h3>
              <div class="p-4">
                @if (savingsDelivery < 0) {
                <p class="mt-0">
                  Du har faktisk spart
                  <strong>{{ (-savingsDelivery).toFixed(0) }} kr</strong>
                  tilknyttet døgnmaks nettleie allerede, bra jobba!
                </p>
                }
                <p class="mt-0 pr-2">
                  @if (isBeginningOfNewMonth) { Her kan du se hvordan du brukte
                  strømmen forrige måned påvirket nettleia. Analyse for
                  inneværende måned kommer når vi har mer data. } @else { Her
                  kan du se hvordan du bruker strømmen inneværende måned med
                  tanke på dine døgnmakser. }
                </p>

                @if (!errorAnalyse) { @if (analyzedConsumption &&
                analyzedConsumption.nettleietrinn) {
                <div class="border border-[--primary-strong] rounded-lg">
                  @if (isBeginningOfNewMonth) {
                  <app-summary-nettleie
                    [analyzedConsumption]="analyzedConsumption.nettleietrinn"
                  ></app-summary-nettleie>
                  } @else {
                  <app-nettleie-advisory
                    [analyzeNettleie]="analyzedConsumption.nettleietrinn"
                    [errorAnalyse]="errorAnalyse"
                  ></app-nettleie-advisory>
                  }
                </div>
                } @if (currentHighThreeDayMax) {
                <div class="border border-[--primary-strong] rounded-lg my-4">
                  <app-daymax-three
                    [currentHighThreeDayMax]="currentHighThreeDayMax"
                  ></app-daymax-three>
                </div>
                } @else {
                <p class="bg-[--warning-40] p-4 rounded-lg m-0">
                  Det skjedde dessverre en feil når vi prøvde å hente døgnmakser
                  for inneværende måned. Prøv igjen senere.
                </p>
                }

                <div
                  class="my-4 py-4 px-2 border border-[--primary-strong] rounded-lg flex items-center"
                >
                  <p class="my-0 ml-0 mr-3">
                    🕵️ Når på døgnet og gjennom uka bruker du generelt mest
                    strøm samtidig?
                  </p>
                  <ion-button class="" id="investigate-dash">
                    Undersøk
                    <ion-icon name="chevron-forward-outline"></ion-icon>
                  </ion-button>
                </div>
                <ion-modal [trigger]="'investigate-dash'" backdropDismiss>
                  <ng-template>
                    <ion-content>
                      <app-prediction-nettleie></app-prediction-nettleie>
                    </ion-content>
                  </ng-template>
                </ion-modal>
                <ion-button class="dark my-4" (click)="goToNettleie()"
                  >Nettleieanalyse
                  <ion-icon name="arrow-forward-outline"></ion-icon></ion-button
                >}@else {
                <p class="bg-[--warning-40] p-4 rounded-lg m-0">
                  Det skjedde dessverre en feil når vi prøvde å hente
                  nettleieanalysen. Prøv igjen senere.
                </p>
                }
              </div>
            </div>
          </ion-col>
        </ion-row>
        <ion-row>
          <ion-col>
            <h2 class="ml-2 mt-0">Forbruksanalyse</h2>
            <div class="bg-white pb-0.5 mb-8 rounded shadow-lg">
              <h3 class="bg-[--primary-strong-100] m-0 p-4 rounded-t-lg">
                <strong
                  >{{
                    savingsConsumption > 0 ? savingsConsumption.toFixed(0) : 0
                  }}
                  kr å spare når prisen er rimeligere</strong
                >
              </h3>
              <div class="p-4">
                @if (savingsConsumption < 0) {
                <p class="mt-0">
                  Du har faktisk spart
                  <strong>{{ (-savingsConsumption).toFixed(0) }} kr</strong>
                  når prisen er rimeligere, bra jobba!
                </p>
                } @if (valgtAnlegg) {
                <app-prices-forbruk-dashboard
                  [objectid]="valgtAnlegg.objectid"
                  chartId="prices-forbruk-chart-dashboard"
                ></app-prices-forbruk-dashboard>
                <ion-button class="dark my-4" (click)="goToForbruksAnalyse()"
                  >Forbruksanalyse
                  <ion-icon name="arrow-forward-outline"></ion-icon
                ></ion-button>
                } @else {
                <p class="bg-[--warning-40] p-4 rounded-lg m-0">
                  Det skjedde dessverre en feil når vi prøvde å hente
                  forbruksanalysen. Prøv igjen senere.
                </p>
                }
              </div>
            </div>
          </ion-col>
        </ion-row>
      </ion-col>
    </ion-row>
  `,
  styleUrl: './dashboard.component.scss'
})
export class AnalyseDashboardComponent {
  @Input() analyzedConsumption: AnalyzedConsumption;
  @Input() smartScore: SmartScore;
  @Input() currentHighThreeDayMax: Hours[];
  @Input() errorAnalyse: boolean;
  @Input() errorScore: boolean;
  @Input() hasNoConfig: boolean;
  @Input() netteier: string;
  @Input() valgtAnlegg: Anlegg;
  @Input() meteorologyObservations: MeteorologyObservations;
  @Input() meteorologyForecast: MeteorologyForecast;

  isBeginningOfNewMonth: boolean = false;
  isPastSmartScore: boolean = true;
  isPushAvailable: boolean;
  monthLabel: string;

  savings: number = 0;
  savingsDelivery: number = 0;
  savingsConsumption: number = 0;

  constructor(
    private _analytics: AnalyticsService,
    private navCtrl: NavController,
    private _alertController: AlertController,
    private router: Router
  ) {
    const now = new Date();
    this.isBeginningOfNewMonth = now.getDate() < newAnalysisDay;
    this.isPastSmartScore = now.getDate() < newAnalysisDay;
    this.isPushAvailable = Capacitor.isPluginAvailable('PushNotifications');
  }

  async ngOnInit() {
    if (this.smartScore) {
      this.calculateSavings();
      this.monthLabel = formatYYYYMMNumberToDate(
        this.smartScore.period
      ).toLocaleDateString('nb-NO', {
        month: 'long'
      });
    }
    // Only show once
    const { value } = await Preferences.get({
      key: 'hasReadAnalyseDayMaxNews'
    });
    if (!value || value === 'false') {
      this.showNewsAlert();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (this.smartScore) {
      this.calculateSavings();
      this.monthLabel = formatYYYYMMNumberToDate(
        this.smartScore.period
      ).toLocaleDateString('nb-NO', {
        month: 'long'
      });
    }
    if (hasChanged([changes.meteorologyObservations])) {
      this.meteorologyObservations =
        changes.meteorologyObservations.currentValue;
    }
    if (hasChanged([changes.meteorologyForecast])) {
      this.meteorologyForecast = changes.meteorologyForecast.currentValue;
    }
  }

  calculateSavings() {
    this.savings = 0;
    this.savingsDelivery = 0;
    this.savingsConsumption = 0;

    if (this.smartScore.state_step_delivery < 0) {
      this.savings += this.smartScore.state_step_delivery * -1;
    }
    this.savingsDelivery += this.smartScore.state_step_delivery * -1;
    if (this.smartScore.state_consumption_days < 0) {
      this.savings += this.smartScore.state_consumption_days * -1;
    }
    this.savingsConsumption += this.smartScore.state_consumption_days * -1;
    if (this.smartScore.state_consumption_hours < 0) {
      this.savings += this.smartScore.state_consumption_hours * -1;
    }
    this.savingsConsumption += this.smartScore.state_consumption_hours * -1;
  }

  goToNettleie() {
    this._analytics.logEvent_selectContent('analysis_nettleie_opened');
    const navigationExtras = {
      state: {
        analyzedConsumption: this.analyzedConsumption.nettleietrinn,
        netteier: this.netteier,
        hasNoConfig: this.hasNoConfig,
        errorAnalyse: this.errorAnalyse,
        valgtAdresse: this.valgtAnlegg.adresse
      }
    };
    this.navCtrl.navigateForward(analyseNettleieRoute, navigationExtras);
  }

  goToForbruksAnalyse() {
    this._analytics.logEvent_selectContent('analysis_smartscore_opened');
    const navigationExtras = {
      state: {
        analyzedConsumption: this.analyzedConsumption,
        smartScore: this.smartScore,
        netteier: this.netteier,
        hasNoConfig: this.hasNoConfig,
        errorAnalyse: this.errorAnalyse,
        objectid: this.valgtAnlegg.objectid,
        valgtAdresse: this.valgtAnlegg.adresse,
        valgtAnlegg: this.valgtAnlegg,
        meteorologyObservations: this.meteorologyObservations,
        meteorologyForecast: this.meteorologyForecast
      }
    };
    this.navCtrl.navigateForward(smartScoreRoute, navigationExtras);
  }

  async showNewsAlert() {
    if (this.isPushAvailable) {
      const initalert = await this._alertController.create({
        cssClass: 'push-alert',
        mode: 'ios',
        header: 'Nyhet!',
        message:
          'Du kan nå få pushvarslinger hvis du når en nettleie-døgnmaks som er høyere enn forventet. Dette er en eksperimentell funksjon som jobbes med fortløpende. Gå til Profil for å administrere varsler.',
        buttons: [
          {
            text: 'Lukk',
            role: 'cancel'
          },
          {
            text: 'Profil',
            role: 'confirm',
            handler: () => {
              this.router.navigate([profilRoute]);
            }
          }
        ]
      });
      await initalert.present();

      await Preferences.set({
        key: 'hasReadAnalyseDayMaxNews',
        value: 'true'
      });
    }
  }
}
